/**
 * Orchestration for the in-place "rebuild & reload" control (WEB-ONLY).
 *
 * Holds what the UI needs: the latest status snapshot, whether the collector is
 * reachable, and the live phase/log while a rebuild runs. The actual polling
 * cadence (every 60s + on focus for status; ~1.5s while a rebuild runs) lives in
 * the RebuildControl component; this store just does the fetches and the state
 * transitions, so the reload-on-done behaviour is in one testable place.
 *
 * Note: this is SEPARATE from src/store/update.ts, which is the Android APK
 * in-app updater. Different mechanism (APK from CI vs. rebuild on the PC),
 * different platform (native vs. web), so a separate store keeps them from
 * tangling.
 */
import { create } from 'zustand';
import {
  fetchUpdateStatus,
  postUpdateRun,
  fetchUpdateProgress,
  type UpdateStatus,
  type UpdatePhase,
} from '@/feedback/updateClient';

/** Reload the page so the freshly-built dist is picked up. Guarded for SSR/tests. */
function reloadPage(): void {
  if (typeof window !== 'undefined' && window.location) window.location.reload();
}

interface AppUpdateState {
  /** Last status snapshot from the collector, or null before the first poll. */
  status: UpdateStatus | null;
  /** Did the collector answer the last status poll? Drives "quietly hide on no server". */
  reachable: boolean;
  /** True once we've triggered a rebuild and are polling its progress. */
  running: boolean;
  /** Live rebuild phase. */
  phase: UpdatePhase;
  /** Live build flag from the server (true while pull+build runs). */
  building: boolean;
  /** Last ~40 masked log lines, for the details view. */
  tail: string[];
  /** A surfaced error (a failed run, or a run that couldn't start). */
  error: string | null;

  /** Poll GET /update/status and fold it in. */
  refresh: (collectorUrl?: string, token?: string) => Promise<void>;
  /** Trigger a rebuild (POST /update/run). Sets running on success. */
  startRebuild: (collectorUrl?: string, token?: string) => Promise<void>;
  /** Poll GET /update/progress once; reloads on 'done', surfaces on 'error'. */
  pollProgress: (collectorUrl?: string, token?: string) => Promise<void>;
}

export const useAppUpdateStore = create<AppUpdateState>((set, get) => ({
  status: null,
  reachable: false,
  running: false,
  phase: 'idle',
  building: false,
  tail: [],
  error: null,

  refresh: async (collectorUrl, token) => {
    const s = await fetchUpdateStatus(collectorUrl, token);
    if (!s) {
      // Collector unreachable: keep the last status but mark it not reachable, so
      // the control quietly hides rather than showing a stale banner.
      set({ reachable: false });
      return;
    }
    set({ status: s, reachable: true, building: s.building });
  },

  startRebuild: async (collectorUrl, token) => {
    if (get().running || get().building) return;
    set({ error: null });
    const r = await postUpdateRun(collectorUrl, token);
    if (!r) {
      set({ error: 'Could not reach the PC to start a rebuild.' });
      return;
    }
    if (!r.started) {
      // Already building, or nothing to do — reflect it without an alarming error.
      if (r.reason && r.reason !== 'up to date' && r.reason !== 'already building') {
        set({ error: r.reason });
      }
      return;
    }
    set({ running: true, phase: 'pulling', building: true, tail: [], error: null });
  },

  pollProgress: async (collectorUrl, token) => {
    const p = await fetchUpdateProgress(collectorUrl, token);
    if (!p) return; // a blip; keep polling, the next tick may land
    set({ phase: p.phase, building: p.building, tail: p.tail });
    if (p.phase === 'done') {
      set({ running: false });
      reloadPage();
      return;
    }
    if (p.phase === 'error') {
      set({ running: false, error: p.lastBuild?.error ?? 'Rebuild failed.' });
    }
  },
}));
